import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, doc, addDoc, deleteDoc, updateDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../firebase/config';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardFooter } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const EMPTY = { name: '', description: '', mapper: '', tier: '', imageUrl: '' };

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const MapManagement = () => {
  const { tournamentId } = useParams();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imageUpload, setImageUpload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMaps = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'tournaments', tournamentId, 'maps'));
      setMaps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load maps.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tournamentId) fetchMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setImageUpload(null);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '',
      description: m.description || '',
      mapper: m.mapper || '',
      tier: m.tier || '',
      imageUrl: m.imageUrl || '',
    });
    setImageUpload(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required.');
      return;
    }
    setSaving(true);
    try {
      let imageUrl = form.imageUrl;
      if (imageUpload) {
        const imgRef = ref(
          storage,
          `tournaments/${tournamentId}/maps/${editing?.id || 'new'}_${imageUpload.name}`
        );
        await uploadBytes(imgRef, imageUpload);
        imageUrl = await getDownloadURL(imgRef);
      }

      const payload = { ...form, imageUrl };
      if (editing) {
        await updateDoc(doc(db, 'tournaments', tournamentId, 'maps', editing.id), payload);
        toast.success('Map updated.');
      } else {
        await addDoc(collection(db, 'tournaments', tournamentId, 'maps'), {
          ...payload,
          createdAt: new Date(),
        });
        toast.success('Map added.');
      }
      setDialogOpen(false);
      await fetchMaps();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.imageUrl) {
        try {
          await deleteObject(ref(storage, deleteTarget.imageUrl));
        } catch {
          // image may already be gone — non-fatal
        }
      }
      await deleteDoc(doc(db, 'tournaments', tournamentId, 'maps', deleteTarget.id));
      toast.success('Map deleted.');
      setDeleteTarget(null);
      await fetchMaps();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  if (!tournamentId) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-16 text-center text-sm text-muted-foreground">
        Select a tournament in the top bar to manage its map pool.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Add map
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-60 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : maps.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Empty
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            No maps yet. Add the first one to get started.
          </p>
          <div className="mt-5">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add map
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {maps.map((m) => (
            <Card key={m.id} className="overflow-hidden border-border/60 bg-background/40">
              {m.imageUrl ? (
                <div className="h-40 w-full overflow-hidden border-b border-border/60">
                  <img src={m.imageUrl} alt="" className="h-full w-full object-cover opacity-90" />
                </div>
              ) : (
                <div className="h-3 w-full bg-gradient-to-r from-primary/30 via-heat/20 to-transparent" />
              )}
              <CardContent className="space-y-3 pt-5">
                <h3 className="truncate font-display text-xl font-semibold tracking-tight">
                  {m.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {m.tier && <Badge variant="outline">Tier {m.tier}</Badge>}
                  {m.mapper && <Badge variant="outline">by {m.mapper}</Badge>}
                </div>
                {m.description && (
                  <p className="line-clamp-3 text-sm text-muted-foreground">{m.description}</p>
                )}
              </CardContent>
              <CardFooter className="justify-between gap-2 border-t border-border/60 pt-4">
                <Button variant="ghost" size="sm" onClick={() => openEdit(m)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(m)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit map' : 'Add map'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <Field label="Map name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Mapper">
                <Input
                  value={form.mapper}
                  onChange={(e) => setForm({ ...form, mapper: e.target.value })}
                />
              </Field>
              <Field label="Tier">
                <Input
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  placeholder="e.g. T3"
                />
              </Field>
            </div>
            <Field label="Description">
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
            <Field label="Map image">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageUpload(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
              />
              {(imageUpload || form.imageUrl) && (
                <img
                  src={imageUpload ? URL.createObjectURL(imageUpload) : form.imageUrl}
                  alt="Map preview"
                  className="mt-3 h-40 w-full rounded-md border border-border/60 object-cover"
                />
              )}
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete map?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> from this
            tournament.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapManagement;
